import React from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { ScanLine, Car, ShieldAlert, History, ClipboardList, LogOut, CircleParking, MapPin, FileSignature, LifeBuoy } from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { useAuth } from "../../context/AuthContext";
import { NotificationsBell } from "../NotificationsBell";
import { useRoleModules } from "../../lib/roleModules";
import { usePolicyAccess } from "../../lib/policies";   // Phase 218: My Policies tab (+ pending count)
import { MarshalProvider, useMarshal } from "../../pages/marshal/marshalShared";
import { ParkingLiveBadge } from "../parking/common";
import { EmptyState } from "../common";

/* Phase 202 — Parking Marshal handheld shell: primary header with posting + shift, bottom thumb-reach nav. */

function Shell({ code, children }) {
  const { auth, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { allowed } = useRoleModules(code);
  const { me, error } = useMarshal();
  // Phase 218: company policies assigned to this marshal — tab appears once selected for any
  const { myTotal: myPolicies, myPending: myPendingPolicies } = usePolicyAccess(code);
  const items = [
    { to: `/${code}/marshal`, label: "Gate", icon: ScanLine, end: true, testId: "marshal-nav-gate", module: "marshal_app" },
    { to: `/${code}/marshal/inside`, label: "Inside", icon: Car, testId: "marshal-nav-inside", module: "marshal_app" },
    { to: `/${code}/marshal/violations`, label: "Violations", icon: ShieldAlert, testId: "marshal-nav-violations", module: "violations" },
    { to: `/${code}/marshal/log`, label: "Log", icon: History, testId: "marshal-nav-log", module: "marshal_app" },
    { to: `/${code}/marshal/shift`, label: "Shift", icon: ClipboardList, testId: "marshal-nav-shift", module: "shift_report" },
  ].filter((it) => allowed(it.module));
  if (myPolicies > 0) items.push({ to: `/${code}/marshal/policies`, label: "Policies", icon: FileSignature, testId: "marshal-nav-policies", badge: myPendingPolicies });   // Phase 218
  const shift = me?.shift_now;
  // Phase 218: My Policies must stay reachable even when the handheld has no posting yet
  const policiesPage = pathname.endsWith("/marshal/policies") || pathname.endsWith("/marshal/help");   // Phase 222: Help stays reachable too
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="sticky top-0 z-40 bg-primary text-primary-foreground px-4 py-3">
        <div className="flex items-center justify-between gap-2 max-w-2xl mx-auto w-full">
          <div className="flex items-center gap-2 min-w-0">
            <div className="h-9 w-9 rounded-lg bg-primary-foreground/15 flex items-center justify-center shrink-0"><CircleParking className="h-5 w-5" /></div>
            <div className="min-w-0">
              <p className="font-semibold text-sm font-heading truncate" data-testid="marshal-header-location">{me?.location?.name || auth?.municipality?.short_name || "Smart Parking"}</p>
              <p className="text-[11px] opacity-80 truncate" data-testid="marshal-header-role">
                Parking Marshal • {auth?.user?.name}{auth?.user?.employee_id ? ` (${auth.user.employee_id})` : ""}{shift ? ` • ${shift.name} shift` : ""}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <ParkingLiveBadge className="hidden sm:inline-flex border-primary-foreground/30 bg-primary-foreground/10 text-primary-foreground" />
            <div className="[&_button]:text-primary-foreground [&_button:hover]:bg-primary-foreground/10"><NotificationsBell code={code} /></div>
            <Button variant="ghost" size="icon" className="text-primary-foreground hover:bg-primary-foreground/10" onClick={() => navigate(`/${code}/marshal/help`)} aria-label="Help" title="Help & App Flow" data-testid="marshal-nav-help"><LifeBuoy className="h-5 w-5" /></Button>
            <Button variant="ghost" size="icon" className="text-primary-foreground hover:bg-primary-foreground/10" onClick={() => { logout(); navigate("/"); }} data-testid="marshal-logout-button"><LogOut className="h-5 w-5" /></Button>
          </div>
        </div>
      </header>
      <main className="flex-1 pb-24 px-3 pt-4 max-w-2xl w-full mx-auto">
        {error && !policiesPage ? (
          <div data-testid="marshal-no-location">
            <EmptyState title="Handheld not ready" subtitle={error} />
          </div>
        ) : children}
      </main>
      <nav className="fixed bottom-0 inset-x-0 z-40 bg-card border-t flex justify-around px-2 py-1.5 max-w-2xl mx-auto w-full" data-testid="marshal-bottom-nav">
        {items.map((it) => (
          <NavLink key={it.to} to={it.to} end={it.end} data-testid={it.testId}
            className={({ isActive }) => `relative flex flex-col items-center gap-0.5 px-2 py-1.5 rounded-lg min-w-[56px] min-h-[48px] justify-center ${isActive ? "text-primary" : "text-muted-foreground"}`}>
            <it.icon className="h-5 w-5" />
            <span className="text-[11px] font-medium">{it.label}</span>
            {it.badge > 0 && (
              <span className="absolute top-0.5 right-1 min-w-[16px] h-4 px-1 rounded-full bg-amber-500 text-white text-[10px] font-semibold leading-4 text-center"
                data-testid={`${it.testId}-badge`} aria-label={`${it.badge} pending`}>{it.badge > 9 ? "9+" : it.badge}</span>
            )}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}

export const MarshalLayout = ({ code, children }) => (
  <MarshalProvider code={code}><Shell code={code}>{children}</Shell></MarshalProvider>
);

/** Small posting strip reused on the Gate + Inside screens. */
export const PostingStrip = ({ location, shift, occupancy }) => (
  <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground" data-testid="marshal-posting-strip">
    <span className="inline-flex items-center gap-1 min-w-0"><MapPin className="h-3.5 w-3.5 text-primary shrink-0" /><span className="truncate">{location?.name}</span></span>
    <span className="flex items-center gap-1.5 shrink-0">
      {location?.open_now ? <Badge variant="outline" className="border-[hsl(var(--success))] text-[hsl(var(--success))] text-[10px]">Open</Badge> : <Badge variant="secondary" className="text-[10px]">Closed</Badge>}
      {shift && <Badge variant="outline" className="text-[10px]">{shift.name} {shift.start}–{shift.end}</Badge>}
      {occupancy && <span className="font-semibold tabular-nums text-foreground">{occupancy.total?.available ?? 0} free</span>}
    </span>
  </div>
);
